import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../contexts/AuthContext";
import cachedHttpClient from "../../services/cached-http-client";
import { useAppDispatch } from "../../store/hooks";
import { sessionReset } from "../../store/session-reset";

/**
 * AD-608. Кэш данных принадлежит пользователю. Выход — переход внутри SPA без
 * перезагрузки, а ключи запросов пользователя не содержат и при повторном
 * монтировании не перезапрашиваются (`refetchOnMount: false`, свежесть 5 минут):
 * без сброса следующий, кто войдёт в той же вкладке, видел данные прежнего.
 *
 * Сброс — по смене пользователя, а не в каждой точке выхода: так покрыты
 * `logout`, `logoutAll`, выход по отказу тихого обновления токена и вход другим
 * пользователем. Первый вход (гость → пользователь) кэш не трогает — чужих
 * данных в нём нет. Путь 401 перезагружает страницу и кэш теряет сам.
 *
 * AD-747(б). Под React Query у аналитики и отчётов свой кэш ответов —
 * `cachedHttpClient`: 5 минут, общий на вкладку и на оба экземпляра клиента,
 * пользователя в ключе нет. Сбрасывается здесь же, первым; ответы, бывшие в пути
 * в момент сброса, в него уже не лягут (поколение кэша, `cached-http-client.ts`).
 *
 * Стоит под `QueryClientProvider`, `Provider store` и `AuthProvider` (`App.tsx`).
 */
export function SessionCacheReset(): null {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const previousUserIdRef = useRef<string | null>(userId);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (previousUserId !== null && previousUserId !== userId) {
      cachedHttpClient.invalidateCache();
      queryClient.clear();
      dispatch(sessionReset());
    }
  }, [userId, queryClient, dispatch]);

  return null;
}
